import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import axiosInstance from "@/lib/axios";
import type { RequestGroup } from "@curai/js-fhir-tools";

export enum LabOrderEntryAction {
  CHANGE_LAB_NETWORK = "change-lab-network-available",
  CLIENT_PAY_AVAILABLE = "client-pay-available",
  INSURANCE_BILLING = "insurance-pay-available",
  LAB_SUBMISSION = "lab-submission-available",
}

export enum MissingPatientField {
  FIRST_NAME = "FIRST_NAME",
  LAST_NAME = "LAST_NAME",
  DOB = "DOB",
  GENDER = "GENDER",
  PHONE_NUMBER = "PHONE_NUMBER",
}

type LabOrderEntryActions = {
  changeLabNetworkAvailable: boolean;
  clientPayAvailable: boolean;
  insurancePayAvailable: boolean;
  labSubmissionAvailable: boolean;
  enabledNetworks: (typeof RequestGroup.LAB_NETWORKS)[keyof typeof RequestGroup.LAB_NETWORKS][];
  patientOrderingAvailability: PatientOrderingAvailability;
};

interface LabOrderEntryActionItem {
  action: LabOrderEntryAction;
  available: boolean;
}

interface PatientOrderingAvailability {
  orderingAvailable: boolean;
  missingFields: MissingPatientField[];
}

interface LabOrderEntryActionsResponse {
  availableActions: LabOrderEntryActionItem[];
  enabledNetworks: (typeof RequestGroup.LAB_NETWORKS)[keyof typeof RequestGroup.LAB_NETWORKS][];
  patientOrderingAvailability: PatientOrderingAvailability;
}

export const useLabOrderEntryActions = ({
  patientId,
}: { patientId: string }) => {
  return useApi<LabOrderEntryActionsResponse, LabOrderEntryActions>({
    queryOptions: {
      enabled: Boolean(patientId),
      queryKey: QueryKeys.Patient.Chart.Labs.ORDER_ENTRY_ACTIONS({
        patientId,
      }),
      queryFn: async () => {
        const response = await axiosInstance.get(
          "/labs/order/order-entry-available-actions",
          {
            params: {
              patientId,
            },
          },
        );
        return response.data;
      },
      select: (data) => {
        const actions = data.availableActions.reduce((acc, item) => {
          if (item.action === LabOrderEntryAction.CHANGE_LAB_NETWORK) {
            acc.changeLabNetworkAvailable = item.available;
          } else if (item.action === LabOrderEntryAction.CLIENT_PAY_AVAILABLE) {
            acc.clientPayAvailable = item.available;
          } else if (item.action === LabOrderEntryAction.INSURANCE_BILLING) {
            acc.insurancePayAvailable = item.available;
          } else if (item.action === LabOrderEntryAction.LAB_SUBMISSION) {
            acc.labSubmissionAvailable = item.available;
          }
          return acc;
        }, {} as LabOrderEntryActions);
        return {
          ...actions,
          enabledNetworks: data.enabledNetworks || [],
          patientOrderingAvailability: data.patientOrderingAvailability,
        };
      },
    },
  });
};
